import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '../services/api';
import { parseServerDate } from '../utils/serverDate';
import '../pages/admin/Admin.css';

// A render error here must never take the Admin page down with it.
class AdminSearchCostsBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.error('AdminSearchCosts failed to render:', err); }
  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

const DASH = '—';
const isNil = (v) => v === null || v === undefined || v === '' || Number.isNaN(Number(v));
const money = (v, d = 2) => (isNil(v) ? DASH : `$${Number(v).toFixed(d)}`);
const num = (v) => (isNil(v) ? DASH : Number(v).toLocaleString());
const pct = (v) => (isNil(v) ? DASH : `${(Number(v) * 100).toFixed(0)}%`);
const text = (v) => (v === null || v === undefined || v === '' ? DASH : String(v));
const marginColor = (v) => (isNil(v) ? undefined : Number(v) < 0 ? 'var(--danger)' : 'var(--success)');

// places + paid requests; em dash only when both are unknown
const requests = (j) => {
  if (isNil(j.places_requests) && isNil(j.paid_lookup_attempts)) return DASH;
  return String((Number(j.places_requests) || 0) + (Number(j.paid_lookup_attempts) || 0));
};
const paidFound = (j) => (isNil(j.paid_lookup_found) && isNil(j.paid_lookup_attempts)
  ? DASH
  : `${isNil(j.paid_lookup_found) ? DASH : j.paid_lookup_found}/${isNil(j.paid_lookup_attempts) ? DASH : j.paid_lookup_attempts}`);

// {reason: count} from pipeline_stats.site_failures; em dash for searches run before it existed
const failures = (j) => {
  const f = j.site_failures;
  if (!f || typeof f !== 'object') return DASH;
  const parts = Object.entries(f).map(([k, v]) => `${k} ${v}`);
  return parts.length ? parts.join(', ') : 'none';
};

const muted = { color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 };

function Stat({ label, value, color, tone = 0 }) {
  return (
    <div className={`stat-card admin-stat admin-stat-${tone} admin-mini-stat`}>
      <div className="stat-value" style={color ? { color } : undefined}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function AdminSearchCostsInner() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminAPI.getSearchStats(50);
      const d = res?.data || {};
      setData({
        jobs: Array.isArray(d.jobs) ? d.jobs : [],
        totals: d.totals || {},
        settings: d.cost_settings || {},
      });
      setError(false);
    } catch (err) {
      setError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  let body;
  if (error) {
    body = (
      <p style={{ ...muted, color: 'var(--danger)' }}>
        Couldn't load search costs.{' '}
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setError(false); setData(null); load(); }}>Try again</button>
      </p>
    );
  } else if (data === null) {
    body = <p style={muted}>Loading...</p>;
  } else {
    const { jobs, totals: t, settings: s } = data;
    body = (
      <>
        <div className="stats-grid admin-mini-grid">
          <Stat tone={0} label="Searches" value={num(t.searches)} />
          <Stat tone={2} label="ScaleSerp requests" value={num(t.scaleserp_requests)} />
          <Stat tone={3} label="Vendor cost" value={money(t.vendor_cost)} />
          <Stat tone={1} label="Revenue est." value={money(t.revenue_estimate)} />
          <Stat tone={1} label="Margin" value={money(t.margin)} color={marginColor(t.margin)} />
          <Stat tone={0} label="Paid lookup success" value={pct(t.paid_lookup_success_rate)} />
          <Stat tone={2} label="Cost per extra email" value={money(t.paid_lookup_cost_per_email, 4)} />
        </div>
        <p style={{ ...muted, fontSize: '0.75rem', margin: '0.5rem 0 0.75rem' }}>
          Cost per request {money(s.scaleserp_cost_per_request, 4)} · value per credit {money(s.credit_value_usd, 2)}.
          {' '}Estimates: cost per request and value per credit come from the server settings.
        </p>
        {jobs.length === 0 ? (
          <p style={muted}>No searches yet.</p>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>User</th>
                  <th>Industry / Location</th>
                  <th>Status</th>
                  <th>Found</th>
                  <th>Added</th>
                  <th>Credits</th>
                  <th>Requests</th>
                  <th>Paid found/attempts</th>
                  <th>Guessed emails</th>
                  <th>Site failures</th>
                  <th>Vendor cost</th>
                  <th>Revenue est.</th>
                  <th>Margin</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j, i) => (
                  <tr key={j.id ?? i}>
                    <td>{parseServerDate(j.created_at)?.toLocaleString() || DASH}</td>
                    <td>{text(j.user_email)}</td>
                    <td>{text(j.industry)} / {text(j.location)}</td>
                    <td>{text(j.status)}</td>
                    <td>{num(j.buyers_found)}</td>
                    <td>{num(j.buyers_added)}</td>
                    <td>{num(j.credits_spent)}</td>
                    <td>{requests(j)}</td>
                    <td>{paidFound(j)}</td>
                    <td>{num(j.emails_guessed)}</td>
                    <td>{failures(j)}</td>
                    <td>{money(j.vendor_cost)}</td>
                    <td>{money(j.revenue_estimate)}</td>
                    <td style={{ color: marginColor(j.margin), fontWeight: 650 }}>{money(j.margin)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="admin-search-costs">
      {body}
    </div>
  );
}

function AdminSearchCosts() {
  return (
    <AdminSearchCostsBoundary>
      <AdminSearchCostsInner />
    </AdminSearchCostsBoundary>
  );
}

export default AdminSearchCosts;
